"use client"

import type React from "react"

/**
 * @fileoverview iOS Safari detection and fix hooks
 * @description Handles iOS 26 Safari viewport bugs with fixed positioning
 * @module MobileBottomNav/useIOSSafariFix
 * @version 1.0.0
 *
 * Known iOS 26 Safari Bugs (December 2025):
 * - Fixed elements shift when address bar shrinks/expands
 * - visualViewport.offsetTop doesn't reset after keyboard dismissal
 * - 100dvh creates gaps at bottom for overlays
 * - position: fixed breaks after keyboard interaction
 *
 * This hook provides workarounds for these issues.
 */

import { useEffect, useRef, useCallback } from "react"

/**
 * iOS version detection result
 * @interface IOSVersion
 */
interface IOSVersion {
  /** Whether the device is running iOS */
  isIOS: boolean
  /** Whether the browser is Safari */
  isSafari: boolean
  /** Major iOS version number (e.g., 26 for iOS 26) */
  majorVersion: number | null
  /** Minor iOS version number */
  minorVersion: number | null
  /** Whether this is iOS 26 or later with known bugs */
  hasViewportBugs: boolean
}

/**
 * Detects iOS version and Safari browser
 * @returns {IOSVersion} iOS detection result
 */
export function detectIOSVersion(): IOSVersion {
  if (typeof window === "undefined" || typeof navigator === "undefined") {
    return {
      isIOS: false,
      isSafari: false,
      majorVersion: null,
      minorVersion: null,
      hasViewportBugs: false,
    }
  }

  const ua = navigator.userAgent
  const isIOS = /iPad|iPhone|iPod/.test(ua) || (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1)
  const isSafari = /Safari/.test(ua) && !/Chrome|CriOS|FxiOS|EdgiOS/.test(ua)

  let majorVersion: number | null = null
  let minorVersion: number | null = null

  // Extract iOS version from user agent
  const versionMatch = ua.match(/OS (\d+)_(\d+)/)
  if (versionMatch) {
    majorVersion = Number.parseInt(versionMatch[1], 10)
    minorVersion = Number.parseInt(versionMatch[2], 10)
  }

  // iOS 26 (which would be shown as version 26.x) has known viewport bugs
  // Note: iOS versions might appear as 18.x in some contexts due to marketing vs. internal versioning
  const hasViewportBugs = isIOS && majorVersion !== null && majorVersion >= 18

  return {
    isIOS,
    isSafari,
    majorVersion,
    minorVersion,
    hasViewportBugs,
  }
}

/**
 * Configuration for iOS Safari fix
 * @interface IOSSafariFixConfig
 */
export interface IOSSafariFixConfig {
  /** Enable scroll-based body fix (moves scroll from window to body) */
  enableScrollFix?: boolean
  /** Enable keyboard visibility detection */
  enableKeyboardFix?: boolean
  /** Callback when keyboard visibility changes */
  onKeyboardVisibilityChange?: (visible: boolean) => void
  /** Enable debug logging */
  debug?: boolean
}

/**
 * Hook to apply iOS Safari viewport bug fixes
 *
 * @param {IOSSafariFixConfig} config - Configuration options
 * @returns {{ isKeyboardVisible: boolean; iosInfo: IOSVersion }} Hook state
 *
 * @example
 * ```tsx
 * const { isKeyboardVisible, iosInfo } = useIOSSafariFix({
 *   enableScrollFix: true,
 *   enableKeyboardFix: true,
 *   onKeyboardVisibilityChange: (visible) => console.log('Keyboard:', visible)
 * });
 * ```
 */
export function useIOSSafariFix(config: IOSSafariFixConfig = {}) {
  const { enableScrollFix = true, enableKeyboardFix = true, onKeyboardVisibilityChange, debug = false } = config

  const iosInfoRef = useRef<IOSVersion>(detectIOSVersion())
  const isKeyboardVisibleRef = useRef(false)
  const initialViewportHeightRef = useRef<number | null>(null)

  const log = useCallback(
    (message: string, ...args: unknown[]) => {
      if (debug) {
        console.log(`[iOS Safari Fix] ${message}`, ...args)
      }
    },
    [debug],
  )

  // Apply scroll fix for iOS 26
  useEffect(() => {
    const iosInfo = iosInfoRef.current

    if (!iosInfo.hasViewportBugs || !enableScrollFix) {
      return
    }

    log("Applying iOS Safari scroll fix")

    // Add class to enable CSS workarounds
    document.documentElement.classList.add("ios-safari-fix")

    if (iosInfo.majorVersion !== null && iosInfo.majorVersion >= 18) {
      document.documentElement.classList.add("ios-26-fix")
    }

    return () => {
      document.documentElement.classList.remove("ios-safari-fix", "ios-26-fix")
    }
  }, [enableScrollFix, log])

  // Keyboard visibility detection
  useEffect(() => {
    const iosInfo = iosInfoRef.current

    if (!iosInfo.isIOS || !enableKeyboardFix) {
      return
    }

    // Store initial viewport height
    if (typeof window !== "undefined" && window.visualViewport) {
      initialViewportHeightRef.current = window.visualViewport.height
    }

    const handleViewportResize = () => {
      if (!window.visualViewport || initialViewportHeightRef.current === null) {
        return
      }

      const currentHeight = window.visualViewport.height
      const heightDifference = initialViewportHeightRef.current - currentHeight

      // If viewport shrunk by more than 150px, keyboard is likely visible
      const keyboardVisible = heightDifference > 150

      if (keyboardVisible !== isKeyboardVisibleRef.current) {
        isKeyboardVisibleRef.current = keyboardVisible

        log("Keyboard visibility changed:", keyboardVisible)

        if (keyboardVisible) {
          document.documentElement.classList.add("keyboard-visible")
          document.documentElement.classList.remove("keyboard-dismissed")
        } else {
          document.documentElement.classList.remove("keyboard-visible")
          document.documentElement.classList.add("keyboard-dismissed")

          // Remove the dismissed class after animation completes
          setTimeout(() => {
            document.documentElement.classList.remove("keyboard-dismissed")
          }, 100)

          // iOS 26 fix: Force layout recalculation after keyboard dismissal
          if (iosInfo.hasViewportBugs) {
            requestAnimationFrame(() => {
              // Force a reflow to reset fixed positioning
              document.body.style.display = "none"
              // eslint-disable-next-line @typescript-eslint/no-unused-expressions
              document.body.offsetHeight // Trigger reflow
              document.body.style.display = ""
            })
          }
        }

        onKeyboardVisibilityChange?.(keyboardVisible)
      }
    }

    const handleFocusIn = (e: FocusEvent) => {
      const target = e.target as HTMLElement
      if (target.tagName === "INPUT" || target.tagName === "TEXTAREA" || target.isContentEditable) {
        log("Input focused, preparing for keyboard")
      }
    }

    const handleFocusOut = () => {
      // Give time for visualViewport to update
      setTimeout(() => {
        if (window.visualViewport && initialViewportHeightRef.current) {
          const heightDiff = initialViewportHeightRef.current - window.visualViewport.height
          if (heightDiff < 50) {
            // Keyboard is likely hidden, trigger fix
            if (iosInfoRef.current.hasViewportBugs) {
              log("Focus out detected, applying iOS 26 fix")
              document.documentElement.classList.add("keyboard-dismissed")
              setTimeout(() => {
                document.documentElement.classList.remove("keyboard-dismissed")
              }, 100)
            }
          }
        }
      }, 100)
    }

    window.visualViewport?.addEventListener("resize", handleViewportResize)
    document.addEventListener("focusin", handleFocusIn)
    document.addEventListener("focusout", handleFocusOut)

    return () => {
      window.visualViewport?.removeEventListener("resize", handleViewportResize)
      document.removeEventListener("focusin", handleFocusIn)
      document.removeEventListener("focusout", handleFocusOut)
      document.documentElement.classList.remove("keyboard-visible", "keyboard-dismissed")
    }
  }, [enableKeyboardFix, onKeyboardVisibilityChange, log])

  return {
    isKeyboardVisible: isKeyboardVisibleRef.current,
    iosInfo: iosInfoRef.current,
  }
}

/**
 * Hook to force reset fixed element positioning after iOS viewport bugs
 *
 * @param {React.RefObject<HTMLElement>} elementRef - Ref to the fixed element
 * @returns {{ forceReset: () => void }} Reset function
 *
 * @example
 * ```tsx
 * const navRef = useRef<HTMLElement>(null);
 * const { forceReset } = useIOSFixedReset(navRef);
 *
 * // Call forceReset() after keyboard dismissal or scroll issues
 * ```
 */
export function useIOSFixedReset(elementRef: React.RefObject<HTMLElement | null>) {
  const forceReset = useCallback(() => {
    const element = elementRef.current
    if (!element) return

    // Force GPU layer reset
    element.style.transform = "translateZ(0) translateY(0.01px)"

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        if (element) {
          element.style.transform = "translateZ(0)"
        }
      })
    })
  }, [elementRef])

  return { forceReset }
}

export default useIOSSafariFix
